import { FC } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { u } from '../common/Units';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Short delay before the loader appears, so fast responses do not flicker
const appear = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Wrapper = styled.div<{ $variant: LoaderVariant }>`
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: 0;
  animation: ${appear} 0.3s ease 0.25s forwards;

  /* Centered in the nearest positioned parent (page or panel) */
  ${p => (p.$variant === "page" || p.$variant === "fill") && `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  `}

  /* Below the content, e.g. when more results are loaded */
  ${p => p.$variant === "inline" && `
    padding: ${u(40)} 0;
  `}
`;

const Spinner = styled.svg<{ $size: number }>`
  width: ${p => u(p.$size)};
  height: ${p => u(p.$size)};
  animation: ${spin} 0.9s linear infinite;

  & circle {
    fill: none;
    stroke: currentColor;
    stroke-width: 3;
  }

  & .track {
    opacity: 0.18;
  }

  & .arc {
    stroke-linecap: round;
    /* Quarter of the circle (2πr = 2π·20 ≈ 125.7) */
    stroke-dasharray: 31.4 125.7;
    opacity: 0.85;
  }
`;

export type LoaderVariant = "page" | "fill" | "inline";

interface LoaderProps {
  /**
   * "page" and "fill": centered in the nearest positioned parent (the whole page or a panel).
   * "inline": centered horizontally in the flow, e.g. below the results
   */
  variant?: LoaderVariant,
  /** Diameter in kiosk units */
  size?: number,
  /** Color. Defaults to the text color of the parent */
  color?: string
}

/**
 * Centered loading indicator (DM views, Minne popup): a thin ring with a rotating arc.
 * Uses currentColor, so it follows the readable text color of the background
 * @returns {JSX.Element} Component template
 */
const Loader: FC<LoaderProps> = ({ variant = "page", size, color }) => {
  const { t } = useTranslation();
  const diameter = size || (variant === "inline" ? 48 : 72);
  return (
    <Wrapper $variant={variant} role="status" aria-live="polite" aria-label={t("dm.loading")} style={color ? { color } : undefined}>
      <Spinner $size={diameter} viewBox="0 0 48 48" aria-hidden="true">
        <circle className="track" cx="24" cy="24" r="20"/>
        <circle className="arc" cx="24" cy="24" r="20"/>
      </Spinner>
    </Wrapper>
  );
}

export default Loader;
